import React, { useState } from 'react';
import { Activity, Users, Building2, Shield, FileText, ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useUserStore } from '../../stores/userStore';
import { useListingStore } from '../../stores/listingStore';
import { useLeaseStore } from '../../stores/leaseStore';
import { formatDate } from '../../utils/formatters';
import { resetAllStores } from '../../utils/resetStores';

const AdminDashboard = () => {
  const { users } = useUserStore();
  const { listings } = useListingStore();
  const { requests } = useLeaseStore();
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const stats = {
    totalUsers: {
      value: Object.keys(users).length,
      change: 12,
      trend: 'up'
    },
    activeListings: {
      value: listings.filter(l => l.status === 'active').length,
      change: 8,
      trend: 'up'
    },
    pendingVerifications: {
      value: Object.values(users).filter(u => 
        u.verifications?.id?.status === 'pending' || 
        u.verifications?.address?.status === 'pending'
      ).length,
      change: -5,
      trend: 'down'
    },
    pendingApplications: {
      value: requests.filter(r => r.status === 'pending').length,
      change: 15,
      trend: 'up'
    }
  };

  const handleReset = () => {
    resetAllStores();
  };

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard 
            title="Total Users" 
            value={stats.totalUsers.value}
            change={stats.totalUsers.change}
            trend={stats.totalUsers.trend}
            icon={Users}
          />
          <StatCard 
            title="Active Listings" 
            value={stats.activeListings.value}
            change={stats.activeListings.change}
            trend={stats.activeListings.trend}
            icon={Building2}
          />
          <StatCard 
            title="Pending Verifications" 
            value={stats.pendingVerifications.value}
            change={stats.pendingVerifications.change}
            trend={stats.pendingVerifications.trend}
            icon={Shield}
          />
          <StatCard 
            title="Pending Applications" 
            value={stats.pendingApplications.value}
            change={stats.pendingApplications.change}
            trend={stats.pendingApplications.trend}
            icon={FileText}
          />
        </div>

        {/* Reset Data Section */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Reset Data</h2>
              <p className="text-sm text-gray-500">Clear all data and start fresh</p>
            </div>
            {showResetConfirm ? (
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setShowResetConfirm(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReset}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Confirm Reset
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowResetConfirm(true)}
                className="flex items-center px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Reset All Data
              </button>
            )}
          </div>
        </div>

        {/* Rest of the dashboard content */}
      </div>
    </AdminLayout>
  );
};

const StatCard = ({ title, value, change, trend, icon: Icon }: any) => (
  <div className="bg-white rounded-xl shadow-sm p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-600 mb-1">{title}</p>
        <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
        {change !== undefined && (
          <p className={`text-sm mt-1 ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {trend === 'up' ? <ArrowUp className="h-4 w-4 inline mr-1" /> : <ArrowDown className="h-4 w-4 inline mr-1" />}
            {Math.abs(change)}% from last month
          </p>
        )}
      </div>
      <div className="h-12 w-12 bg-indigo-100 rounded-full flex items-center justify-center">
        <Icon className="h-6 w-6 text-indigo-600" />
      </div>
    </div>
  </div>
);

export default AdminDashboard;